import React, { useState, useEffect } from 'react';
import { FiCalendar, FiClock, FiUsers } from 'react-icons/fi';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { BASE_URL, PUT_SUBSCRIPTION_EXPIRY_DATE, GET_SUBSCRIPTION_EXPIRY_DATE } from '../../utils/config';
import { useNavigate } from 'react-router-dom';

const UpdateSubscription = () => {
  const [expiryDate, setExpiryDate] = useState('');

  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [timeLeft, setTimeLeft] = useState(60);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [pendingDate, setPendingDate] = useState('');
  const [currentExpiryDate, setCurrentExpiryDate] = useState('');
  const [isLoadingExpiryDate, setIsLoadingExpiryDate] = useState(false);
  
  // License count states
  const [licenseCount, setLicenseCount] = useState('');
  const [showLicenseVerificationModal, setShowLicenseVerificationModal] = useState(false);
  const [licenseVerificationCode, setLicenseVerificationCode] = useState('');
  const [licenseTimeLeft, setLicenseTimeLeft] = useState(60);
  const [isSubmittingLicense, setIsSubmittingLicense] = useState(false);
  const [isVerifyingLicense, setIsVerifyingLicense] = useState(false);
  const [isSendingLicenseCode, setIsSendingLicenseCode] = useState(false);
  const [pendingLicenseCount, setPendingLicenseCount] = useState('');
  
  const token = localStorage.getItem('accessToken');

  const [clientInfo, setClientInfo] = useState({});

  const isElectron = () => {
    return window && window.process && window.process.type;
  };

  let ipcRenderer;

  if (isElectron()) {
    ipcRenderer = window.require('electron').ipcRenderer;
  }

  // Timer effect for subscription expiry
  useEffect(() => {
    let interval = null;
    if (showVerificationModal && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else if (timeLeft === 0 && showVerificationModal) {
      setShowVerificationModal(false);
      toast.error('Verification code expired. Please request a new code.', { autoClose: 3000 });
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [showVerificationModal, timeLeft]);

  // Timer effect for license count
  useEffect(() => {
    let interval = null;
    if (showLicenseVerificationModal && licenseTimeLeft > 0) {
      interval = setInterval(() => {
        setLicenseTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else if (licenseTimeLeft === 0 && showLicenseVerificationModal) {
      setShowLicenseVerificationModal(false);
      toast.error('Verification code expired. Please request a new code.', { autoClose: 3000 });
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [showLicenseVerificationModal, licenseTimeLeft]);

  useEffect(() => {
    const fetchData = async () => {
      let client_info;

      if (isElectron()) {
        client_info = await ipcRenderer.invoke('store-get', 'clientInfo');
      }

      console.log('clientInfo', client_info);
      setClientInfo(client_info || {});
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchCurrentExpiryDate = async () => {

      setIsLoadingExpiryDate(true);
      try {
        const response = await axios.get(`${BASE_URL}${GET_SUBSCRIPTION_EXPIRY_DATE}`, {
          headers: {
            'XApiKey': process.env.REACT_APP_API_KEY || '',
            'Authorization': `Bearer ${token}`
          }
        });

        const expiryDateStr = response.data?.data?.subscriptionExpiryDate;
        if (expiryDateStr) {
          setCurrentExpiryDate(expiryDateStr);
        }
      } catch (error) {
        console.error('Error fetching current expiry date:', error);
        // Don't show error toast here as it might be expected if user doesn't have permission
      } finally {
        setIsLoadingExpiryDate(false);
      }
    };

    fetchCurrentExpiryDate();
  }, []);

  const validateDate = () => {
    if (!expiryDate.trim()) {
      toast.error('Please select an expiry date', { autoClose: 3000 });
      return false;
    }

    const selectedDate = new Date(expiryDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (selectedDate < today) {
      toast.error('Expiry date cannot be in the past', { autoClose: 3000 });
      return false;
    }

    return true;
  };

  const sendVerificationCode = async () => {
    setIsSendingCode(true);
    try {
      // Use Django mail service
      const DJANGO_MAIL_URL = process.env.REACT_APP_DJANGO_MAIL_URL || 'http://localhost:8000';
      const response = await axios.post(`${DJANGO_MAIL_URL}/send-verification-code/`, {
        email: 'swalihmp438368@gmail.com',
        client_name: clientInfo?.ClientName || 'User',
        msg: `Update subscription expiry date to ${expiryDate}`,
      });

      if (response.data.success) {
        toast.success('Verification code sent to your email', { autoClose: 3000 });
        setShowVerificationModal(true);
        setTimeLeft(60);
        setVerificationCode('');
      } else {
        toast.error(response.data.message || 'Failed to send verification code', { autoClose: 3000 });
      }
    } catch (error) {
      console.error('Error sending verification code:', error);
      toast.error(error.response?.data?.message || error.message || 'Failed to send verification code. Please try again.', { autoClose: 3000 });
    } finally {
      setIsSendingCode(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateDate()) {
      return;
    }

    if (!token) {
      toast.error('Please authenticate first', { autoClose: 2000 });
      return;
    }

    // Store date for later use after verification
    setPendingDate(expiryDate);

    // Send verification code
    await sendVerificationCode();
  };

  const handleVerifyAndSubmit = async () => {
    if (!verificationCode.trim() || verificationCode.length !== 6) {
      toast.error('Please enter a valid 6-digit code', { autoClose: 2000 });
      return;
    }

    setIsVerifying(true);
    try {
      // Verify code using Django mail service
      const DJANGO_MAIL_URL = process.env.REACT_APP_DJANGO_MAIL_URL || 'http://localhost:8000';
      const verifyResponse = await axios.post(`${DJANGO_MAIL_URL}/verify-code/`, {
        email: 'swalihmp438368@gmail.com',
        code: verificationCode,
        client_name: clientInfo?.ClientName || 'User',
      });

      if (!verifyResponse.data.success) {
        toast.error(verifyResponse.data.message || 'Invalid verification code', { autoClose: 3000 });
        setIsVerifying(false);
        return;
      }

      // Code verified, now submit subscription expiry date
      // Format date as YYYY-MM-DD for the API
      let formattedDate = pendingDate;
      
      // Ensure date is in YYYY-MM-DD format
      if (formattedDate) {
        const dateObj = new Date(formattedDate);
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        formattedDate = `${year}-${month}-${day}`;
      }




      const response = await axios.post(
        `${BASE_URL}${PUT_SUBSCRIPTION_EXPIRY_DATE}`,
        {},
        {
          headers: {
            'XApiKey': process.env.REACT_APP_API_KEY || '',
            'Authorization': `Bearer ${token}`,
            'DateToUpdate': formattedDate, // Date in YYYY-MM-DD format in header as per API spec
            'no_licence': clientInfo?.No_Of_Licence,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.status === 200) {
        toast.success('Subscription expiry date updated successfully', { autoClose: 3000 });

        const response = await axios.get(`${BASE_URL}${GET_SUBSCRIPTION_EXPIRY_DATE}`, {
          headers: {
            'XApiKey': process.env.REACT_APP_API_KEY || '',
            'Authorization': `Bearer ${token}`
          }
        });

        const expiryDateStr = response.data?.data?.subscriptionExpiryDate;
        if (expiryDateStr) {
          setCurrentExpiryDate(expiryDateStr);
        }

        setShowVerificationModal(false);
        setVerificationCode('');
        setExpiryDate('');

      } else {
        toast.error('Failed to update subscription expiry date', { autoClose: 3000 });
      }
    } catch (error) {
      console.error('Error verifying code or updating subscription:', error);
      if (error.response?.status === 400) {
        toast.error(error.response?.data?.message || 'Invalid verification code', { autoClose: 3000 });
      } else {
        toast.error(error.response?.data?.message || 'Failed to update subscription expiry date. Please try again.', { autoClose: 3000 });
      }
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendCode = async () => {
    await sendVerificationCode();
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // License count functions
  const validateLicenseCount = () => {
    if (!licenseCount || licenseCount.trim() === '') {
      toast.error('Please enter a license count', { autoClose: 3000 });
      return false;
    }

    const count = parseInt(licenseCount, 10);
    if (isNaN(count) || count < 1) {
      toast.error('License count must be a positive number', { autoClose: 3000 });
      return false;
    }

    return true;
  };

  const sendLicenseVerificationCode = async () => {
    setIsSendingLicenseCode(true);
    try {
      const DJANGO_MAIL_URL = process.env.REACT_APP_DJANGO_MAIL_URL || 'http://localhost:8000';
      const oldLicenseCount = clientInfo?.No_Of_Licence || 0;
      const response = await axios.post(`${DJANGO_MAIL_URL}/send-verification-code/`, {
        email: 'swalihmp438368@gmail.com',
        client_name: clientInfo?.ClientName || 'User',
        msg: `Add device count from ${oldLicenseCount} to ${licenseCount}`,
      });

      if (response.data.success) {
        toast.success('Verification code sent to your email', { autoClose: 3000 });
        setShowLicenseVerificationModal(true);
        setLicenseTimeLeft(60);
        setLicenseVerificationCode('');
      } else {
        toast.error(response.data.message || 'Failed to send verification code', { autoClose: 3000 });
      }
    } catch (error) {
      console.error('Error sending verification code:', error);
      toast.error(error.response?.data?.message || error.message || 'Failed to send verification code. Please try again.', { autoClose: 3000 });
    } finally {
      setIsSendingLicenseCode(false);
    }
  };

  const handleLicenseSubmit = async (e) => {
    e.preventDefault();

    if (!validateLicenseCount()) {
      return;
    }

    if (!token) {
      toast.error('Please authenticate first', { autoClose: 2000 });
      return;
    }

    // Store license count for later use after verification
    setPendingLicenseCount(licenseCount);

    // Send verification code
    await sendLicenseVerificationCode();
  };

  const handleVerifyAndUpdateLicense = async () => {
    if (!licenseVerificationCode.trim() || licenseVerificationCode.length !== 6) {
      toast.error('Please enter a valid 6-digit code', { autoClose: 2000 });
      return;
    }

    setIsVerifyingLicense(true);
    try {
      // Verify code using Django mail service
      const DJANGO_MAIL_URL = process.env.REACT_APP_DJANGO_MAIL_URL || 'http://localhost:8000';
      const verifyResponse = await axios.post(`${DJANGO_MAIL_URL}/verify-code/`, {
        email: 'swalihmp438368@gmail.com',
        code: licenseVerificationCode,
        client_name: clientInfo?.ClientName || 'User',
      });

      if (!verifyResponse.data.success) {
        toast.error(verifyResponse.data.message || 'Invalid verification code', { autoClose: 3000 });
        setIsVerifyingLicense(false);
        return;
      }

      // Code verified, now update license count using the same API as subscription expiry
      const newLicenseCount = parseInt(pendingLicenseCount, 10);

      // Format current expiry date as YYYY-MM-DD for the API
      let formattedDate = currentExpiryDate;
      if (formattedDate) {
        const dateObj = new Date(formattedDate);
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        formattedDate = `${year}-${month}-${day}`;
      }

      const response = await axios.post(
        `${BASE_URL}${PUT_SUBSCRIPTION_EXPIRY_DATE}`,
        {},
        {
          headers: {
            'XApiKey': process.env.REACT_APP_API_KEY || '',
            'Authorization': `Bearer ${token}`,
            'DateToUpdate': formattedDate, // Current expiry date in YYYY-MM-DD format
            'no_licence': newLicenseCount, // New license count as integer
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.status === 200) {
        toast.success('License count updated successfully', { autoClose: 3000 });
        
        // Update clientInfo with new license count
        const updatedClientInfo = {
          ...clientInfo,
          No_Of_Licence: newLicenseCount
        };

        // Update Electron store
        if (isElectron()) {
          await ipcRenderer.invoke('store-set', 'clientInfo', updatedClientInfo);
        }

        // Update localStorage
        localStorage.setItem('clientInfo', JSON.stringify(updatedClientInfo));

        // Update state
        setClientInfo(updatedClientInfo);
        setLicenseCount('');
        setShowLicenseVerificationModal(false);
        setLicenseVerificationCode('');
        setPendingLicenseCount('');
      } else {
        toast.error('Failed to update license count', { autoClose: 3000 });
      }
    } catch (error) {
      console.error('Error verifying code or updating license count:', error);
      if (error.response?.status === 400) {
        toast.error(error.response?.data?.message || 'Invalid verification code', { autoClose: 3000 });
      } else {
        toast.error(error.response?.data?.message || 'Failed to update license count. Please try again.', { autoClose: 3000 });
      }
    } finally {
      setIsVerifyingLicense(false);
    }
  };

  const handleResendLicenseCode = async () => {
    await sendLicenseVerificationCode();
  };

  // Get today's date in YYYY-MM-DD format for min attribute
  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="p-6 w-full">
      <ToastContainer position="top-right" autoClose={3000} />
      <style>{`
        .Toastify__toast-container {
          z-index: 10000 !important;
        }
        .Toastify__toast {
          z-index: 10000 !important;
        }
      `}</style>

      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
          <FiCalendar size={32} className="text-blue-600" />
          Update Subscription Expiry Date
        </h1>
        <p className="text-gray-500 mt-2">Update the subscription expiry date for your account</p>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        {currentExpiryDate && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-gray-700">
              <span className="font-semibold">Current Expiry Date:</span>{' '}
              <span className="text-blue-700">
                {new Date(currentExpiryDate).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </span>
            </p>
          </div>
        )}
        {isLoadingExpiryDate && (
          <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <p className="text-sm text-gray-600">Loading current expiry date...</p>
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              New Expiry Date
            </label>
            <input
              type="date"
              value={expiryDate}
              onChange={(e) => setExpiryDate(e.target.value)}
              min={today}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Select expiry date"
              required
            />
            <p className="text-gray-500 text-sm mt-2">
              Select a date on or after today. The date will be updated after verification.
            </p>
          </div>

          <button
            type="submit"
            disabled={isSubmitting || isSendingCode}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSendingCode ? 'Sending Code...' : 'Update Subscription'}
          </button>
        </form>
      </div>

      {/* Verification Modal */}
      {showVerificationModal && (
        <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black bg-opacity-70 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 mb-4">
                <FiClock className="text-blue-600" size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Enter Verification Code</h3>
              <p className="text-gray-600 text-sm mb-4">
                We've sent a 6-digit verification code to swalihmp438368@gmail.com
              </p>
              <div className="flex items-center justify-center gap-2 text-red-600 font-semibold">
                <FiClock size={18} />
                <span>Time remaining: {formatTime(timeLeft)}</span>
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Verification Code
              </label>
              <input
                type="text"
                value={verificationCode}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setVerificationCode(value);
                }}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center text-2xl font-bold tracking-widest"
                placeholder="000000"
                maxLength={6}
                autoFocus
              />
            </div>

            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">
                <strong>New Expiry Date:</strong> {new Date(pendingDate).toLocaleDateString()}
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleVerifyAndSubmit}
                disabled={isVerifying || verificationCode.length !== 6 || timeLeft === 0}
                className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isVerifying ? 'Verifying...' : 'Verify & Update'}
              </button>
              <button
                onClick={handleResendCode}
                disabled={isSendingCode || timeLeft > 0}
                className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSendingCode ? 'Sending...' : 'Resend'}
              </button>
              <button
                onClick={() => {
                  setShowVerificationModal(false);
                  setVerificationCode('');
                  setTimeLeft(60);
                }}
                className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Update License Count Section */}
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 mt-6">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
            <FiUsers size={28} className="text-green-600" />
            Update License Count
          </h2>
          <p className="text-gray-500 mt-2">Update the number of licenses for your account</p>
        </div>

        {clientInfo?.No_Of_Licence && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm text-gray-700">
              <span className="font-semibold">Current License Count:</span>{' '}
              <span className="text-green-700 font-bold text-lg">
                {clientInfo.No_Of_Licence} license(s)
              </span>
            </p>
          </div>
        )}

        <form onSubmit={handleLicenseSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              New License Count
            </label>
            <input
              type="number"
              value={licenseCount}
              onChange={(e) => setLicenseCount(e.target.value)}
              min="1"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="Enter number of licenses"
              required
            />
            <p className="text-gray-500 text-sm mt-2">
              Enter the new number of licenses. The count will be updated after verification.
            </p>
          </div>

          <button
            type="submit"
            disabled={isSubmittingLicense || isSendingLicenseCode}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSendingLicenseCode ? 'Sending Code...' : 'Update License Count'}
          </button>
        </form>
      </div>

      {/* License Verification Modal */}
      {showLicenseVerificationModal && (
        <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black bg-opacity-70 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
                <FiClock className="text-green-600" size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Enter Verification Code</h3>
              <p className="text-gray-600 text-sm mb-4">
                We've sent a 6-digit verification code to swalihmp438368@gmail.com
              </p>
              <div className="flex items-center justify-center gap-2 text-red-600 font-semibold">
                <FiClock size={18} />
                <span>Time remaining: {formatTime(licenseTimeLeft)}</span>
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Verification Code
              </label>
              <input
                type="text"
                value={licenseVerificationCode}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setLicenseVerificationCode(value);
                }}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 text-center text-2xl font-bold tracking-widest"
                placeholder="000000"
                maxLength={6}
                autoFocus
              />
            </div>

            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">
                <strong>New License Count:</strong> {pendingLicenseCount} license(s)
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleVerifyAndUpdateLicense}
                disabled={isVerifyingLicense || licenseVerificationCode.length !== 6 || licenseTimeLeft === 0}
                className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isVerifyingLicense ? 'Verifying...' : 'Verify & Update'}
              </button>
              <button
                onClick={handleResendLicenseCode}
                disabled={isSendingLicenseCode || licenseTimeLeft > 0}
                className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSendingLicenseCode ? 'Sending...' : 'Resend'}
              </button>
              <button
                onClick={() => {
                  setShowLicenseVerificationModal(false);
                  setLicenseVerificationCode('');
                  setLicenseTimeLeft(60);
                }}
                className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UpdateSubscription;
